import {Button} from '@wandb/weave/components/Button';
import {Icon} from '@wandb/weave/components/Icon';
import {Pill, Tag} from '@wandb/weave/components/Tag';
import {Tooltip} from '@wandb/weave/components/Tooltip';
import React, {useEffect, useState} from 'react';

import {StyledTextArea} from '../../../StyledTextarea';
import {PlaygroundMessageRole, PlaygroundState} from '../types';

type PlaygroundChatInputProps = {
  chatText: string;
  setChatText: (text: string) => void;
  isLoading?: boolean;
  onSend?: (role: PlaygroundMessageRole, chatText: string) => void;
  onAdd: (role: PlaygroundMessageRole, chatText: string) => void;
  hasConfiguredProviders?: boolean;
  defaultMessageRole?: PlaygroundMessageRole;
  disableSend?: string;
  cancelGeneration?: () => void;
  playgroundStates?: PlaygroundState[];
  variableValues?: Record<string, string>;
  setVariableValues?: (variableValues: Record<string, string>) => void;
  variableMap?: Record<string, number[]>;
};

/**
 * Scans tracecall.inputs.messages across all playground states for values enclosed in {} single brackets
 * @param playgroundStates - Array of playground states to scan
 * @returns Object where keys are the variable names (without brackets) and values are arrays of state indices where they appear
 */
export const scanSingleBracketVariables = (
  playgroundStates: PlaygroundState[],
  callIndex?: number
): Record<string, number[]> => {
  const variableMap: Record<string, number[]> = {};

  // Regular expression to match {variable} patterns
  const singleBracketRegex = /\{([a-zA-Z][a-zA-Z0-9_]*)\}/g;

  if (callIndex !== undefined) {
    playgroundStates = playgroundStates.slice(callIndex, callIndex + 1);
  }

  playgroundStates.forEach((state, stateIndex) => {
    // Prioritize prompt.messages over traceCall.inputs.messages since prompt.messages
    // contains the actively edited template
    const messages = [
      ...(state.prompt?.messages || []),
      ...(state.traceCall?.inputs?.messages || []),
    ] as any[] | undefined;

    if (!messages) {
      return;
    }

    messages.forEach((message: any) => {
      // Skip assistant messages - they contain LLM responses, not templates
      if (
        !message ||
        typeof message !== 'object' ||
        message.role === 'assistant'
      ) {
        return;
      }

      // Check message content for bracket variables
      if (typeof message.content === 'string') {
        let match;
        while ((match = singleBracketRegex.exec(message.content)) !== null) {
          const variableName = match[1].trim();

          if (!variableMap[variableName]) {
            variableMap[variableName] = [];
          }

          // Add state index if not already present
          if (!variableMap[variableName].includes(stateIndex)) {
            variableMap[variableName].push(stateIndex);
          }
        }
      }

      // Check tool_calls if they exist
      if (message.tool_calls && Array.isArray(message.tool_calls)) {
        message.tool_calls.forEach((toolCall: any) => {
          if (
            toolCall.function &&
            typeof toolCall.function.arguments === 'string'
          ) {
            let match;
            singleBracketRegex.lastIndex = 0;
            while (
              (match = singleBracketRegex.exec(toolCall.function.arguments)) !==
              null
            ) {
              const variableName = match[1].trim();

              if (!variableMap[variableName]) {
                variableMap[variableName] = [];
              }

              // Add state index if not already present
              if (!variableMap[variableName].includes(stateIndex)) {
                variableMap[variableName].push(stateIndex);
              }
            }
          }
        });
      }
    });
  });

  return variableMap;
};

export const PlaygroundChatInput: React.FC<PlaygroundChatInputProps> = ({
  chatText,
  setChatText,
  isLoading = false,
  onSend,
  onAdd,
  hasConfiguredProviders = true,
  defaultMessageRole = 'user',
  disableSend,
  cancelGeneration,
  playgroundStates,
  variableValues,
  setVariableValues,
  variableMap,
}) => {
  const disableChatInput =
    isLoading ||
    (chatText.trim() === '' &&
      Object.keys(variableValues || {}).length === 0) ||
    !hasConfiguredProviders ||
    !!disableSend;

  const [addMessageRole, setAddMessageRole] =
    useState<PlaygroundMessageRole>(defaultMessageRole);
  const [shouldReset, setShouldReset] = useState(false);
  const [isSendButtonHovered, setIsSendButtonHovered] = useState(false);

  const handleReset = () => {
    setShouldReset(true);
    setTimeout(() => setShouldReset(false), 0);
  };

  const handleSend = (role: PlaygroundMessageRole) => {
    onSend?.(role, chatText);
    handleReset();
  };

  const handleAdd = (role: PlaygroundMessageRole, text: string) => {
    onAdd(role, text);
    handleReset();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey && !disableChatInput) {
      event.preventDefault();
      handleSend(addMessageRole);
    }
  };

  useEffect(() => {
    for (const state of playgroundStates ?? []) {
      let templateVars = state.traceCall?.inputs?.template_vars;
      // Remove template vars that are not in the variable map
      if (templateVars && typeof templateVars === 'object' && variableMap) {
        const filteredTemplateVars = Object.keys(templateVars)
          .filter(key => key in variableMap)
          .reduce(
            (acc, key) => {
              acc[key] = templateVars[key];
              return acc;
            },
            {} as typeof templateVars
          );

        if (
          Object.keys(filteredTemplateVars).length !==
          Object.keys(templateVars).length
        ) {
          templateVars = filteredTemplateVars;
        }
      }
      // Only update variableValues if we have template vars and they're not empty
      // This prevents clearing existing values during retry or other operations
      if (
        templateVars &&
        typeof templateVars === 'object' &&
        Object.keys(templateVars).length > 0
      ) {
        // Merge template vars with existing values
        const mergedValues = {
          ...(variableValues || {}),
          ...templateVars,
        };

        // Only update if the values are actually different to avoid infinite loop
        const hasChanges = Object.keys(mergedValues).some(
          key => mergedValues[key] !== variableValues?.[key]
        );

        if (hasChanges) {
          setVariableValues?.(mergedValues);
        }
        break;
      }
    }
  }, [playgroundStates, variableMap, variableValues, setVariableValues]);

  return (
    // WARN: z-index position of navbar overflow menu is `2`, check first if changing
    <div className="z-1 flex min-h-[160px] w-full flex-shrink-0 bg-white pb-16">
      <div className="mx-auto w-full max-w-[800px]">
        {onSend && (
          <div className="mb-8 flex items-center justify-end gap-4 text-xs text-moon-500">
            Press Enter to send
            <Tooltip
              trigger={<Icon className="w-14 h-14" name="help-alt" />}
              content="Hold Shift + Enter to create a newline"
            />
          </div>
        )}
        {variableMap && Object.keys(variableMap).length > 0 ? (
          <div className="group outline outline-moon-250 outline-1 rounded-sm hover:outline-teal-500/40 focus-within:outline-teal-500/40 hover:outline-2 focus-within:outline-2 mb-8">
            <div className="rounded-t-sm grid grid-cols-[auto_1fr_auto] max-h-[148px] overflow-y-scroll">
              {Object.keys(variableMap).map((variable, index) => (
                <React.Fragment key={variable}>
                  <div
                    className={`max-w-[150px] min-w-0 px-8 py-6 border-b border-moon-250 flex items-center ${index === 0 ? 'rounded-t-sm' : ''}`}>
                    <Pill label={variable} color="moon" maxChars={15} />
                  </div>
                  <div className="py-6 border-b border-moon-250 flex items-center">
                    <input
                      type="text"
                      placeholder="Enter variable value"
                      value={variableValues?.[variable] || ''}
                      onChange={e =>
                        setVariableValues?.({
                          ...(variableValues || {}),
                          [variable]: e.target.value,
                        })
                      }
                      className="bg-white px-3 py-2 text-sm placeholder-moon-500 focus:outline-none w-full"
                    />
                  </div>
                  <div className="pl-8 pr-16 py-6 border-b border-moon-250 flex items-center gap-1">
                    {playgroundStates &&
                      variableMap[variable].length !==
                        playgroundStates.length &&
                      variableMap[variable].map(stateIndex => (
                        <Tag
                          key={stateIndex}
                          label={(stateIndex + 1).toString()}
                        />
                      ))}
                  </div>
                </React.Fragment>
              ))}
            </div>
            <StyledTextArea
              className="rounded-t-none !outline-none"
              onChange={e => setChatText(e.target.value)}
              value={chatText}
              onKeyDown={handleKeyDown}
              placeholder={
                hasConfiguredProviders
                  ? 'Type your message here...'
                  : 'Configure a provider to start chatting...'
              }
              autoGrow
              maxHeight={160}
              reset={shouldReset}
              disabled={!hasConfiguredProviders}
            />
          </div>
        ) : (
          <StyledTextArea
            className="mb-8"
            onChange={e => setChatText(e.target.value)}
            value={chatText}
            onKeyDown={handleKeyDown}
            placeholder={
              hasConfiguredProviders
                ? 'Type your message here...'
                : 'Configure a provider to start chatting...'
            }
            autoGrow
            maxHeight={160}
            reset={shouldReset}
            disabled={!hasConfiguredProviders}
          />
        )}
        <div className="flex justify-between">
          <div className="flex gap-8">
            {/* TODO: Add image upload */}
            {/* <Button variant="secondary" size="small" startIcon="photo" /> */}
          </div>
          <div className="flex gap-8">
            <div className="flex items-center text-xs text-moon-500">
              Add as
              <Button
                className="ml-4 rounded-r-none"
                variant="secondary"
                size="medium"
                active={addMessageRole === 'system'}
                onClick={() => setAddMessageRole('system')}>
                System
              </Button>
              <Button
                className="rounded-none"
                variant="secondary"
                size="medium"
                active={addMessageRole === 'assistant'}
                onClick={() => setAddMessageRole('assistant')}>
                Assistant
              </Button>
              <Button
                className="rounded-l-none"
                variant="secondary"
                size="medium"
                active={addMessageRole === 'user'}
                onClick={() => setAddMessageRole('user')}>
                User
              </Button>
            </div>
            <Button
              variant="secondary"
              size="medium"
              startIcon="add-new"
              disabled={
                isLoading || chatText.trim() === '' || !hasConfiguredProviders
              }
              onClick={() => handleAdd(addMessageRole, chatText)}>
              Add
            </Button>
            {onSend && (
              <>
                <div className="h-full w-px bg-moon-250" />
                <Button
                  size="medium"
                  onClick={() => {
                    if (isLoading && cancelGeneration) {
                      cancelGeneration();
                    } else {
                      handleSend(addMessageRole);
                    }
                  }}
                  onMouseEnter={() => setIsSendButtonHovered(true)}
                  onMouseLeave={() => setIsSendButtonHovered(false)}
                  disabled={disableChatInput && !isLoading}
                  className={isLoading ? 'opacity-35' : ''}
                  startIcon={
                    isLoading && isSendButtonHovered ? 'close' : undefined
                  }
                  tooltip={disableSend || undefined}>
                  {isLoading
                    ? isSendButtonHovered
                      ? 'Cancel'
                      : 'Sending...'
                    : 'Send'}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
